/**
 * Maps the All Runs URL search onto filter values (and back). The list is
 * server-paginated, so these stay in the URL rather than in React state —
 * Back/Forward and a shared link restore the same view.
 */

import { z } from 'zod';
import {
  parsePageSizeOption,
  type PageSizeOption,
} from './hooks/createWorkflowRunFiltersApi';
import {
  RUN_STATUS_OPTIONS,
  isRunStatusOption,
  type RunStatusOption,
  type TriggerSourceFilter,
} from './hooks/workflowRunQuery';

export const ALL_WORKFLOW_RUNS_SEARCH_FROM =
  '/authenticated/workflows/runs' as const;

const WORKFLOW_ID_SCHEMA = z.uuid();

/**
 * datetime-local (`YYYY-MM-DDTHH:mm`) plus optional seconds / timezone.
 * Calendar fields are checked so `2026-02-30T10:00` cannot roll into March.
 */
const TRIGGERED_AT_SEARCH_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?$/;

export function isValidTriggeredAtSearchValue(value: string): boolean {
  const match = TRIGGERED_AT_SEARCH_PATTERN.exec(value);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] ?? '0');
  if (month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) {
    return false;
  }
  const reconstructed = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second),
  );
  return (
    reconstructed.getUTCFullYear() === year &&
    reconstructed.getUTCMonth() === month - 1 &&
    reconstructed.getUTCDate() === day
  );
}

const triggeredAtSearchSchema = z
  .string()
  .refine(isValidTriggeredAtSearchValue)
  .optional()
  .catch(undefined);

export const allWorkflowRunsSearchSchema = z.object({
  search: z.string().optional(),
  status: z.enum(RUN_STATUS_OPTIONS).optional().catch(undefined),
  triggeredBy: z.enum(['user', 'system']).optional().catch(undefined),
  triggeredFrom: triggeredAtSearchSchema,
  triggeredTo: triggeredAtSearchSchema,
  // A lone `?workflowIds=uuid` is a string; wrap it so the route still loads.
  workflowIds: z
    .union([z.array(z.string()), z.string().transform((id) => [id])])
    .optional()
    .catch(undefined),
  children: z.coerce.boolean().optional(),
  system: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  pageSize: z.coerce.number().int().min(1).max(100).optional().catch(undefined),
});

export type AllWorkflowRunsSearch = z.infer<typeof allWorkflowRunsSearchSchema>;

export const DEFAULT_ALL_RUNS_PAGE_SIZE: PageSizeOption = 25;

export function statusesFromAllRunsSearch(
  search: AllWorkflowRunsSearch,
): RunStatusOption[] {
  return search.status && isRunStatusOption(search.status)
    ? [search.status]
    : [];
}

export function triggerSourceFromAllRunsSearch(
  search: AllWorkflowRunsSearch,
): TriggerSourceFilter {
  return search.triggeredBy ?? 'all';
}

export function pageSizeFromAllRunsSearch(
  search: AllWorkflowRunsSearch,
): PageSizeOption {
  return parsePageSizeOption(search.pageSize) ?? DEFAULT_ALL_RUNS_PAGE_SIZE;
}

export function pageFromAllRunsSearch(search: AllWorkflowRunsSearch): number {
  return search.page ?? 1;
}

export function workflowIdsFromAllRunsSearch(
  search: AllWorkflowRunsSearch,
): string[] {
  return (search.workflowIds ?? []).filter(
    (id) => WORKFLOW_ID_SCHEMA.safeParse(id).success,
  );
}

export function includeChildRunsFromAllRunsSearch(
  search: AllWorkflowRunsSearch,
): boolean {
  return search.children === true;
}

export function includeSystemManagedFromAllRunsSearch(
  search: AllWorkflowRunsSearch,
): boolean {
  return search.system === true;
}

/** Shared-filter fields only — page size stays so "Clear All" does not resize. */
export function clearedAllWorkflowRunsSearch(): AllWorkflowRunsSearch {
  return {
    search: undefined,
    status: undefined,
    triggeredBy: undefined,
    triggeredFrom: undefined,
    triggeredTo: undefined,
    workflowIds: undefined,
    children: undefined,
    system: undefined,
    page: undefined,
  };
}

export function allRunsStatusSearch(
  statuses: readonly RunStatusOption[],
): Pick<AllWorkflowRunsSearch, 'status'> {
  return { status: statuses.length === 1 ? statuses[0] : undefined };
}

export function allRunsTriggerSourceSearch(
  value: TriggerSourceFilter,
): Pick<AllWorkflowRunsSearch, 'triggeredBy'> {
  return { triggeredBy: value === 'all' ? undefined : value };
}

export function allRunsPageSearch(
  page: number,
): Pick<AllWorkflowRunsSearch, 'page'> {
  return { page: page <= 1 ? undefined : page };
}

export function allRunsPageSizeSearch(
  pageSize: PageSizeOption,
): Pick<AllWorkflowRunsSearch, 'pageSize'> {
  return {
    pageSize: pageSize === DEFAULT_ALL_RUNS_PAGE_SIZE ? undefined : pageSize,
  };
}

export function allRunsWorkflowIdsSearch(
  workflowIds: readonly string[],
): Pick<AllWorkflowRunsSearch, 'workflowIds'> {
  return { workflowIds: workflowIds.length > 0 ? [...workflowIds] : undefined };
}

export function applyAllWorkflowRunsSearchUpdate({
  prev,
  updates,
  resetPage = true,
}: {
  prev: AllWorkflowRunsSearch;
  updates: Partial<AllWorkflowRunsSearch>;
  resetPage?: boolean;
}): AllWorkflowRunsSearch {
  return {
    ...prev,
    ...updates,
    page: resetPage ? undefined : 'page' in updates ? updates.page : prev.page,
  };
}

export function toggleAllRunsWorkflowId({
  current,
  workflowId,
  maxSelected,
}: {
  current: readonly string[];
  workflowId: string;
  maxSelected: number;
}): string[] {
  if (current.includes(workflowId)) {
    return current.filter((id) => id !== workflowId);
  }
  if (current.length >= maxSelected) {
    return [...current];
  }
  return [...current, workflowId];
}

export function nextFocusRunningWorkflowSearch({
  workflowIds,
  statusFilter,
  includeChildRuns,
  workflowId,
}: {
  workflowIds: readonly string[];
  statusFilter: string;
  includeChildRuns: boolean;
  workflowId: string;
}): Partial<AllWorkflowRunsSearch> {
  const alreadyFocused =
    workflowIds.length === 1 &&
    workflowIds[0] === workflowId &&
    statusFilter === 'running' &&
    includeChildRuns;
  if (alreadyFocused) {
    return {
      ...allRunsWorkflowIdsSearch([]),
      ...allRunsStatusSearch([]),
      children: undefined,
    };
  }
  return {
    ...allRunsWorkflowIdsSearch([workflowId]),
    ...allRunsStatusSearch(['running']),
    children: true,
  };
}
